import React, { useMemo } from 'react';

import { PlaceHolderImageGenerationEnum } from 'src/apollo/sites';
import { useThrottledTracker } from 'src/lib/js/hooks/useTracker';
import { formatImageURL } from 'src/lib/js/utilities';
import { DefaultPlaceHolderMenuImage } from 'src/public/components/default_template/menu_section/DefaultPlaceHolderMenuImage';
import { CLICKED_MENU_ITEM_EVENT_NAME, CLICKED_MENU_ITEM_EVENT_NO_NAME_STR, getMenuItemSrcSet } from 'src/public/components/default_template/menu_section/MenuItemCard';
import { TemplateOverrides } from 'src/public/components/default_template/menu_section/menuStylingOverrides';
import { useThemeColorScheme } from 'src/public/components/default_template/meta/useTheme';
import Image from 'src/shared/components/common/Image';
import FormattedPrice from 'src/shared/components/common/price/FormattedPrice';
import { useRestaurant } from 'src/shared/components/common/restaurant_context/RestaurantContext';

import BaseCard from './BaseCard';
import { useReorder } from './ReorderContext';
import type { Item } from './ReorderContext';

type Props = {
  item: Item
  templateOverrides?: TemplateOverrides
  testId?: string
}

const ItemCard = (props: Props) => {
  const { restaurant: { config: { menuConfig } } } = useRestaurant();
  const getColorFromTheme = useThemeColorScheme();
  const { setReorderItem } = useReorder();
  const { track } = useThrottledTracker();
  const { item, templateOverrides } = props;
  const { selection } = props.item;

  const itemDescriptionTextColor = menuConfig?.colors?.secondaryText;
  const descriptionStyle = useMemo(() => {
    const legacyStyle = itemDescriptionTextColor ? { color: itemDescriptionTextColor } : {};

    return getColorFromTheme(theme => {
      const menuOverrides = theme.colorOverrides?.onlineOrderingMenu;
      return { color: menuOverrides?.itemDescription ?? theme.colorScheme.text.secondary };
    }, legacyStyle);
  }, [getColorFromTheme, itemDescriptionTextColor]);

  const image = useMemo(() => {
    let imgSrc = item.imageUrls?.medium || item.imageUrls?.small || item.imageUrls?.large || item.imageUrls?.xs;
    imgSrc = imgSrc && formatImageURL(imgSrc);
    const imgSrcSet = item.imageUrls && getMenuItemSrcSet(item.imageUrls);

    if(imgSrc && imgSrcSet) {
      return <Image src={imgSrc} srcSet={imgSrcSet} className="itemImage" alt="" data-testid={item.selection.name + '-image'} />;
    } else if(templateOverrides?.placeholderImageConfig.generatePlaceholderImage === PlaceHolderImageGenerationEnum.RepeatedText) {
      return <DefaultPlaceHolderMenuImage className="itemImage" templateOverrides={templateOverrides} />;
    } else {
      return null;
    }
  }, [item, templateOverrides]);

  const modifierText = useMemo(() => [...new Set(selection.modifiers?.map(modifier => modifier?.name))]
    .filter(name => Boolean(name))
    .join(' · '), [selection.modifiers]);
  const price = selection.preDiscountPrice || 0;

  return (
    <BaseCard
      data-testid={props.testId}
      onClick={() => {
        track(CLICKED_MENU_ITEM_EVENT_NAME, {
          isReorder: true,
          guid: item.selection.itemGuid,
          name: item.selection.name || CLICKED_MENU_ITEM_EVENT_NO_NAME_STR,
          hasImage: Boolean(item.imageUrls?.raw)
          // readOnly: we don't have a readOnly version for reorder
          // outOfStock: we only show in stock items for reorder
          // descLength: doesn't make sense since we show modifiers instead
          // timeBasedRule: we don't have access to this here
        });
        setReorderItem(item);
      }}>
      <div className="itemInfo" data-testid={`item-${selection.guid}`}>
        <div className="headerText" data-testid="item-text">{selection.name}</div>
        <div className="modifierText" data-testid="item-modifier-text" style={descriptionStyle}>{modifierText}</div>
        <div className="itemPrice" data-testid="item-price">{price ? <FormattedPrice value={price} /> : null}</div>
      </div>
      {image}
    </BaseCard>
  );
};

export default ItemCard;
